import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from '../../hooks/useAuth';
import LeadContacts from './LeadContacts';
import LeadProspects from './LeadProspects';
import LeadOverview from './LeadOverview';
import LeadProspectDetail from './LeadProspectDetail';
import LeadSuppressed from './LeadSuppressed';
import LeadWinBack from './LeadWinBack';
import LeadTrialReady from './LeadTrialReady';
import LeadScripts from './LeadScripts';
import LeadMyLeads from './LeadMyLeads';
import LeadPunchPassUpsell from './LeadPunchPassUpsell';
import LeadNationwideUpsell from './LeadNationwideUpsell';
import { LeadFollowUps, LeadContactLog } from './LeadContacts';
import LeadRecordPanel from './LeadRecordPanel';
import PB101RecordDetail from '../pb101/PB101RecordDetail';

const CLUB_NAMES = { WA: 'West Allis', MF: 'Menomonee Falls', GB: 'Green Bay', DEN: 'Denton', LEW: 'Lewisville', MCK: 'McKinney' };

const TABS = [
  { key: "today",      label: "Today's Contacts" },
  { key: "followups",  label: "Follow-Ups" },
  { key: "myleads",    label: "My Leads" },
  { key: "myfollowups",label: "My Follow-Ups" },
  { key: "contactlog", label: "Contact Log" },
  { key: "suppressed", label: "Suppressed" },
  { key: "prospects",  label: "All Prospects" },
  { key: "winback",    label: "Win Back" },
  { key: "trialready", label: "Trial Ready" },
  { key: "punchpass",  label: "🥊 Punch Pass Upsell" },
  { key: "nationwide", label: "🌐 Nationwide Upsell" },
  { key: "scripts",    label: "Scripts" },
  { key: "overview",   label: "Overview" },
];

export default function LeadModule() {
  const [tab, setTab]               = useState('today');
  const [selectedId, setSelectedId] = useState(null);
  const [viewId, setViewId]         = useState(null);
  const [pb101ViewId, setPb101ViewId] = useState(null);

  const { role } = useAuth();
  const { getAccessTokenSilently } = useAuth0();
  const [worklistStatus, setWorklistStatus] = useState(null); // { ready, completedClubs, pendingClubs } | null (loading) | 'error'
  const [worklistDownloading, setWorklistDownloading] = useState(false);
  // Matches the backend's NON_FD_ROLES gate on the daily-worklist routes
  // (index.js) — Josh himself is role 'sales', not 'admin'/'mloc', so this
  // has to match that set exactly rather than reuse isAdmin/isMloc alone.
  const canDownloadWorklist = ['admin', 'mloc', 'mgr', 'sales', 'manager'].includes(role);
  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://murko-coms-api.azurewebsites.net';
  const AUDIENCE = process.env.REACT_APP_AUTH0_AUDIENCE;

  const refreshWorklistStatus = useCallback(async () => {
    if (!canDownloadWorklist) return;
    try {
      const token = await getAccessTokenSilently({ authorizationParams: { audience: AUDIENCE } });
      const res = await fetch(`${API_BASE}/api/lead/daily-worklist/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Status check failed');
      setWorklistStatus(await res.json());
    } catch (e) {
      setWorklistStatus('error');
    }
  }, [canDownloadWorklist, getAccessTokenSilently, API_BASE, AUDIENCE]);

  useEffect(() => { refreshWorklistStatus(); }, [refreshWorklistStatus]);

  async function downloadWorklist() {
    setWorklistDownloading(true);
    try {
      const token = await getAccessTokenSilently({ authorizationParams: { audience: AUDIENCE } });
      const res = await fetch(`${API_BASE}/api/lead/daily-worklist/export-docx`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Josh_AllClubs_DailyWorkList_${new Date().toISOString().split('T')[0]}.docx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Daily work list download failed. Please try again.');
    } finally {
      setWorklistDownloading(false);
    }
  }

  // BUG FIX (Sprint 19.4): this function only ever checked for source === 't2m'.
  // Everything else — including PB101 follow-up cards — fell into the else
  // branch and got treated as a Lead Engine actionId, opened via
  // LeadRecordPanel. PB101 follow-ups use a completely different ID
  // (ltp_pipeline.ltp_id), so that either 404'd or, worse, coincidentally
  // matched an unrelated Lead Engine action with the same numeric ID.
  // Root cause of Josh's "can't open the PB101 contact card after logging
  // an outcome" report — confirmed against real data (Ken Sandoval, MCK,
  // ltp_id 171) before writing this fix, not guessed at.
  function handleSelectProspect(id, source) {
    if (source === 't2m') {
      window.location.href = `/t2m?trialId=${id}`;
    } else if (source === 'pb101') {
      setPb101ViewId(id);
    } else {
      setViewId(id);
    }
  }

  if (selectedId) {
    return (
      <div className="module-page">
        <LeadProspectDetail
          prospectId={selectedId}
          onBack={() => setSelectedId(null)}
        />
      </div>
    );
  }

  // BUG FIX (Sprint 19.4, second pass): PB101RecordDetail is a normal
  // block-flow component, not an overlay — appending it after the tab
  // content (like LeadRecordPanel, which IS an overlay) rendered it
  // thousands of pixels below the visible page instead of replacing the
  // view. Matching the same early-return swap pattern used for selectedId
  // above, rather than appending alongside existing content.
  if (pb101ViewId) {
    return (
      <div className="module-page">
        <PB101RecordDetail
          recordId={pb101ViewId}
          onBack={() => setPb101ViewId(null)}
          onLogContact={() => setPb101ViewId(null)}
        />
      </div>
    );
  }

  return (
    <div className="module-page">
      <div className="module-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 22 }}>📋</span>
          <h1 className="module-title">Lead Engine</h1>
        </div>
        {canDownloadWorklist && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {worklistStatus === null && (
              <span style={{ fontSize: 12, color: '#888' }}>Checking today's upload…</span>
            )}
            {worklistStatus === 'error' && (
              <span style={{ fontSize: 12, color: '#888' }}>Couldn't check upload status</span>
            )}
            {worklistStatus && worklistStatus !== 'error' && !worklistStatus.ready && (
              <span style={{ fontSize: 12, color: '#aaa' }} title={`Waiting on today's upload: ${worklistStatus.pendingClubs.map(id => CLUB_NAMES[id] || id).join(', ')}`}>
                Waiting on today's upload — {worklistStatus.pendingClubs.map(id => CLUB_NAMES[id] || id).join(', ')}
              </span>
            )}
            <button
              style={{
                background: worklistStatus && worklistStatus.ready ? 'var(--yellow)' : 'transparent',
                color: worklistStatus && worklistStatus.ready ? '#000' : '#666',
                border: '1px solid var(--border)', borderRadius: 8, padding: '8px 16px',
                fontWeight: 700, fontSize: 13,
                cursor: worklistStatus && worklistStatus.ready && !worklistDownloading ? 'pointer' : 'not-allowed',
              }}
              disabled={!worklistStatus || worklistStatus === 'error' || !worklistStatus.ready || worklistDownloading}
              onClick={downloadWorklist}
            >
              📄 {worklistDownloading ? 'Preparing…' : "Download Today's Work List"}
            </button>
          </div>
        )}
      </div>

      <div className="module-tabs" style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
        {TABS.map(t => (
          <button
            key={t.key}
            className={`module-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'today'      && <LeadContacts    onSelectProspect={handleSelectProspect} />}
      {tab === 'followups'  && <LeadFollowUps   onSelectProspect={handleSelectProspect} tab="all" />}
      {tab === 'myfollowups'&& <LeadFollowUps   onSelectProspect={handleSelectProspect} tab="mine" />}
      {tab === 'myleads'    && <LeadMyLeads     onSelectProspect={handleSelectProspect} />}
      {tab === 'contactlog' && <LeadContactLog  />}
      {tab === 'suppressed' && <LeadSuppressed  />}
      {tab === 'prospects'  && <LeadProspects   onSelectProspect={handleSelectProspect} />}
      {tab === 'winback'    && <LeadWinBack     onSelectProspect={handleSelectProspect} />}
      {tab === 'trialready' && <LeadTrialReady  onSelectProspect={handleSelectProspect} />}
      {tab === 'punchpass'  && <LeadPunchPassUpsell />}
      {tab === 'nationwide' && <LeadNationwideUpsell />}
      {tab === 'scripts'    && <LeadScripts     />}
      {tab === 'overview'   && <LeadOverview    />}

      {viewId && (
        <LeadRecordPanel
          actionId={viewId}
          onClose={() => setViewId(null)}
        />
      )}
    </div>
  );
}
